import React, { useState, useEffect } from 'react';
import { FaArrowLeft, FaSync, FaDownload, FaTrash } from 'react-icons/fa';

const LogViewer = ({ socket, onBack }) => {
  const [logs, setLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [logContent, setLogContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [renderKey, setRenderKey] = useState(0);

  useEffect(() => {
    if (!socket) return;

    const handleLogsList = (data) => {
      console.log('Received logs via WebSocket:', data);

      // Clear the fallback timeout since WebSocket worked
      if (window.logTimeout) {
        clearTimeout(window.logTimeout);
        window.logTimeout = null;
      }

      const logsArray = Array.isArray(data) ? data : [];
      console.log('Setting logs state to:', logsArray);
      setLogs(logsArray);
      setLoading(false);
      setRenderKey(prev => prev + 1); // Force re-render
    };

    const handleLogContent = (data) => {
      console.log('Received log content:', data);
      setLogContent(data.content);
      setLoading(false);
    };

    socket.on('logs-list', handleLogsList);
    socket.on('log-content', handleLogContent);
    socket.on('log-deleted', (data) => {
      console.log('Log deleted:', data.filename);
    });
    socket.on('error', (error) => {
      console.error('Socket error:', error);
    });

    // Request logs on mount
    fetchLogs();

    return () => {
      socket.off('logs-list', handleLogsList);
      socket.off('log-content', handleLogContent);
    };
  }, [socket]);

  const fetchLogs = async () => {
    setLoading(true);

    if (socket) {
      console.log('Requesting logs via WebSocket');
      socket.emit('get-logs');

      // Fallback to REST API if WebSocket doesn't respond in 1 second
      const timeoutId = setTimeout(async () => {
        console.log('WebSocket timeout, falling back to REST API');
        try {
          const response = await fetch('/api/logs');
          const data = await response.json();
          console.log('Received logs via REST API:', data);
          setLogs(data || []);
          setLoading(false);
        } catch (error) {
          console.error('Failed to fetch logs via REST API:', error);
          setLoading(false);
        }
      }, 1000);

      // Store timeout ID to clear it if WebSocket responds
      window.logTimeout = timeoutId;
    } else {
      // No WebSocket, use REST API directly
      console.log('No WebSocket, using REST API');
      try {
        const response = await fetch('/api/logs');
        const data = await response.json();
        setLogs(data || []);
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch logs via REST API:', error);
        setLoading(false);
      }
    }
  };

  const handleLogSelect = (logFile) => {
    console.log('Selecting log file:', logFile.name);
    setSelectedLog(logFile);
    setLoading(true);
    setLogContent(''); // Clear previous content
    if (socket) {
      console.log('Socket connected:', socket.connected);
      console.log('Emitting get-log-content for:', logFile.name);
      socket.emit('get-log-content', logFile.name);

      // Test if WebSocket is working by emitting a test event
      socket.emit('test-event', 'test-data');
    } else {
      console.error('No socket connection available');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const downloadLog = () => {
    if (selectedLog && logContent) {
      const blob = new Blob([logContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedLog.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const deleteLog = (logToDelete) => {
    if (window.confirm(`Are you sure you want to delete ${logToDelete.name}?`)) {
      console.log('Deleting log:', logToDelete.name);
      if (socket) {
        socket.emit('delete-log', logToDelete.name);

        // If the deleted log was selected, clear the selection
        if (selectedLog && selectedLog.name === logToDelete.name) {
          setSelectedLog(null);
          setLogContent('');
        }
      }
    }
  };

  const deleteAllLogs = () => {
    if (logs.length === 0) return;
    if (window.confirm('Delete all log files? This cannot be undone.')) {
      if (socket) {
        socket.emit('delete-all-logs');
        setSelectedLog(null);
        setLogContent('');
      }
    }
  };


  return (
    <div key={renderKey} className="log-viewer">
      <div className="log-viewer-header">
        <button className="btn btn-secondary" onClick={onBack}>
          <FaArrowLeft /> Back to Form
        </button>
        <button className="btn btn-secondary" onClick={fetchLogs} disabled={loading}>
          <FaSync /> {loading ? 'Loading...' : 'Refresh'}
        </button>
        <button className="btn btn-danger" onClick={deleteAllLogs} disabled={logs.length === 0}>
          <FaTrash /> Delete All Logs
        </button>
      </div>

      <div className="log-viewer-content">
        <div className="logs-sidebar">
          <h3>📁 Log Files</h3>
          <div>RENDERING: {logs.length} logs</div>
          {Array.isArray(logs) && logs.length > 0 ? (
            <div className="logs-list" style={{ background: '#f0f0f0', padding: '10px' }}>
              {logs.map((log, index) => (
                <div
                  key={log.name}
                  className={`log-item ${selectedLog?.name === log.name ? 'active' : ''}`}
                  style={{
                    background: 'white',
                    border: '1px solid #ccc',
                    padding: '15px',
                    margin: '10px 0',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div onClick={() => handleLogSelect(log)} style={{ flex: 1 }}>
                    <div className="log-filename" style={{ fontWeight: 'bold', color: '#007cba', marginBottom: '5px' }}>
                      {log.name}
                    </div>
                    <div className="log-meta" style={{ fontSize: '0.8rem', color: '#666' }}>
                      <span className="log-date">{formatDate(log.modified)}</span>
                      <span style={{ margin: '0 10px' }}>•</span>
                      <span className="log-size">{formatFileSize(log.size)}</span>
                    </div>
                  </div>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteLog(log);
                    }}
                    style={{
                      marginLeft: '10px',
                      padding: '5px 8px',
                      fontSize: '0.8rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                    title="Delete log file"
                  >
                    <FaTrash size={12} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-logs">
              <p>No log files found</p>
              <p>Run a sync operation to generate logs</p>
            </div>
          )}
        </div>

        <div className="log-content-area">
          {selectedLog ? (
            <>
              <div className="log-content-header">
                <h3>📄 {selectedLog.name}</h3>
                <button
                  className="btn btn-secondary"
                  onClick={downloadLog}
                  disabled={!logContent}
                >
                  <FaDownload /> Download
                </button>
              </div>
              <div className="log-content">
                {loading ? (
                  <div className="loading">Loading log content...</div>
                ) : logContent ? (
                  <pre>{logContent}</pre>
                ) : (
                  <div className="no-content">No content available</div>
                )}
              </div>
            </>
          ) : (
            <div className="no-selection">
              <h3>📋 Log Viewer</h3>
              <p>Select a log file from the left to view its contents</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogViewer;